import {
  ClaimMissionRewardResponse_RewardSummary_BadgeStyle,
  ClaimMissionRewardResponse_SuccessResult,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coins/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {
  Button,
  COLORS,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  Image,
  RollingNumbers,
  SPACING,
  StatusInfoBox,
  Text,
  UnstyledButton,
  tabletAndUpLinaria,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, PropsWithChildren, useCallback} from 'react';
import {useWalletStateManager} from '../../../../../backend-services/Wallet/utils/useWalletStateManager';
import frame from '../../CoinsWalletContent/CoinsMissions/FramedMissionBadge/frame.png';
import {
  REWARD_TOTAL_COUNTUP_DELAY_IN_MS,
  useCoinsClaimSuccessPopup,
} from './useCoinsClaimSuccessPopup';

export const CoinsClaimPopupContentSuccess: FC<{
  coinsData: ClaimMissionRewardResponse_SuccessResult;
  CoinsExplosionAnimation: React.ComponentType<{
    showBodyContent: boolean;
  }>;
  isOpen: boolean;
  onClose: VoidFunction;
  onImpression: VoidFunction;
}> = ({coinsData, CoinsExplosionAnimation, isOpen, onClose, onImpression}) => {
  const {showBodyContent, animatedTotalCoins} = useCoinsClaimSuccessPopup({
    coinsData,
    isOpen,
  });

  const {t} = useTranslation();
  const {isOpen: isWalletOpen, open: openWallet} = useWalletStateManager();

  let ctaLabel = t('Discover other Missions');
  if (isWalletOpen) {
    ctaLabel = t('Continue Shopping');
  }

  const {ref: impressionObserverRef} = useInViewEffect<HTMLDivElement>(
    useCallback(
      (inView: boolean) => {
        if (!inView) {
          return;
        }
        onImpression();
      },
      [onImpression],
    ),
    {triggerOnce: true, threshold: 0.2},
  );

  const handleOnClick = () => {
    if (isWalletOpen) {
      onClose();
      return;
    }

    onClose();
    // Delay wallet opening to avoid overlapping transitions on mobile.
    setTimeout(() => {
      openWallet('coins');
    });
  };

  return (
    <>
      <CoinsExplosionAnimation showBodyContent={showBodyContent} />
      <PopupLayout ref={impressionObserverRef} data-testid="coinsClaimSuccess">
        <CloseButton
          onClick={onClose}
          aria-label={t('Close claimed coins popup')}
        >
          <Icon
            asset={GenericIconName.Close}
            size="S"
            fill={COLORS['Text.White']}
          />
        </CloseButton>
        <OuterPopupContent>
          <CenteredContent>
            <Animation shouldAnimate={showBodyContent}>
              <PopupContent>
                {coinsData?.rewardSummary.imageStyle ===
                ClaimMissionRewardResponse_RewardSummary_BadgeStyle.FRAMED ? (
                  <FramedImageWrapper data-testid="framedImageWrapper">
                    <FramedBackgroundDisc />
                    <FramedCoinImage
                      src={coinsData.rewardSummary.image.url ?? ''}
                      alt={
                        coinsData.rewardSummary.image.description ||
                        coinsData.rewardSummary.headline
                      }
                      breakpoints={[]}
                      importance="high"
                      ratio="1:1"
                      disableFixedRatio
                      widths={[128, 256, 512]}
                    />
                    <FramedOverlay />
                  </FramedImageWrapper>
                ) : (
                  <ImageWrapper data-testid="standardImageWrapper">
                    <ClaimedCoinImage
                      src={coinsData?.rewardSummary.image.url ?? ''}
                      alt={
                        coinsData?.rewardSummary.image.description ||
                        coinsData?.rewardSummary.headline
                      }
                      breakpoints={[]}
                      importance="high"
                      ratio="1:1"
                    />
                  </ImageWrapper>
                )}

                <Headline size="px20" weight="mediumHeavy" color="Text.White">
                  {coinsData?.rewardSummary.headline}
                </Headline>
                <Description size="px14" weight="mediumBold" color="Text.White">
                  {coinsData?.rewardSummary.description}
                </Description>
                {coinsData?.benefit && animatedTotalCoins && (
                  <CoinsBalanceRow>
                    <CoinsBalanceLabel
                      size="px14"
                      weight="mediumBold"
                      color="Text.White"
                    >
                      {coinsData?.rewardSummary.coinsBalance}
                    </CoinsBalanceLabel>
                    <CoinsValueWrapper>
                      <Icon
                        asset={GenericIconName.CoinsIconDark}
                        size="S"
                        fill={COLORS['Text.White']}
                      />
                      <CoinsAmountWrapper>
                        <Text
                          size="px20"
                          weight="mediumHeavy"
                          color="Text.White"
                        >
                          <RollingNumbers
                            value={animatedTotalCoins}
                            numbersWidth={13}
                            customTimingFunction="cubic-bezier(0.47, -0.6, 0.41, 0.8)"
                            alwaysRollUp
                            cascadeOvershoot
                          />
                        </Text>
                        {showBodyContent && (
                          <DeltaAnimationWrapper>
                            <DeltaAnimated>
                              <CoinsDelta
                                size="px12"
                                weight="mediumHeavy"
                                color="Text.White"
                              >
                                +{coinsData?.benefit.reward ?? 0}
                              </CoinsDelta>
                            </DeltaAnimated>
                          </DeltaAnimationWrapper>
                        )}
                      </CoinsAmountWrapper>
                    </CoinsValueWrapper>
                  </CoinsBalanceRow>
                )}
                {coinsData?.rewardSummary.expiring && (
                  <StatusUpdateWrapper>
                    <StyledStatusInfoBox
                      text={coinsData.rewardSummary.expiring}
                      icon={GenericIconName.InfoFilledGray}
                      iconSize="XSS"
                      color="Coins.Orange"
                    />
                  </StatusUpdateWrapper>
                )}
              </PopupContent>
            </Animation>
          </CenteredContent>
        </OuterPopupContent>

        <Footer>
          <ContinueButton
            onClick={handleOnClick}
            size="full-width"
            aria-label={undefined}
          >
            {ctaLabel}
          </ContinueButton>
        </Footer>
      </PopupLayout>
    </>
  );
};

const TIME_TO_FADE_IN_BODY_CONTENT = 400;

const Animation: FC<PropsWithChildren<{shouldAnimate: boolean}>> = ({
  children,
  shouldAnimate,
}) => {
  return (
    <Fade mounted={shouldAnimate} duration={TIME_TO_FADE_IN_BODY_CONTENT}>
      {children}
    </Fade>
  );
};
const PopupLayout = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 2;

  ${tabletAndUpLinaria(`
    min-height: 100%;
    padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  `)}
`;

const CloseButton = styled(UnstyledButton)`
  position: absolute;
  top: ${SPACING['Y.Sm']};
  right: ${SPACING['X.Sm']};
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;

  ${tabletAndUpLinaria(`
    top: ${SPACING['Y.Lg']};
    left: ${SPACING['X.Lg']};
    right: auto;
  `)}
`;

const CoinsDelta = styled(Text)`
  align-self: center;
  line-height: 1.5;
`;

const OuterPopupContent = styled.div`
  display: flex;

  ${tabletAndUpLinaria(`
    flex: 1;
  `)}
`;

const CenteredContent = styled.div`
  width: 100%;

  ${tabletAndUpLinaria(`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  `)}
`;

const PopupContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: ${SPACING['Y.Md']};
  text-align: center;
  position: relative;
  padding: calc(${SPACING['Y.Xl']} + 16px) ${SPACING['X.Lg']} 0;
`;

const Headline = styled(Text)`
  line-height: 1.2;
`;

const Description = styled(Text)`
  line-height: 1.5;
`;

const CoinsBalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING['X.Sm']};
  margin-top: ${SPACING['Y.Xl']};
`;

const CoinsBalanceLabel = styled(Text)`
  line-height: 1.5;
`;

const CoinsValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING['X.Sm']};
`;

const CoinsAmountWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const DeltaAnimationWrapper = styled.div`
  position: absolute;
  left: calc(100% + ${SPACING['X.Sm']});
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  pointer-events: none;
`;

const DeltaAnimated = styled.div`
  opacity: 0;
  transform: translateY(16px);
  animation: coinsRewardDeltaSequence 1700ms cubic-bezier(0.22, 0.61, 0.36, 1)
    ${REWARD_TOTAL_COUNTUP_DELAY_IN_MS}ms both;

  @keyframes coinsRewardDeltaSequence {
    0% {
      opacity: 0;
      transform: translateY(16px);
    }
    20% {
      opacity: 1;
      transform: translateY(0);
    }
    65% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(-16px);
    }
  }
`;

const StatusUpdateWrapper = styled.div`
  margin-top: ${SPACING['Y.Md']};
`;

const Footer = styled.div`
  margin-top: ${SPACING['Y.Xl']};
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};

  ${tabletAndUpLinaria(`
    margin-top: auto;
  `)}
`;

const StyledStatusInfoBox = styled(StatusInfoBox)`
  height: 22px;
`;

const ContinueButton = styled(Button)`
  width: 100%;
  min-height: 48px;
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
`;

const ImageWrapper = styled.div`
  --coin-image-size: 173px;
  --shimmer-circle-inset: 22px;
  width: var(--coin-image-size);
  height: var(--coin-image-size);
  flex: 0 0 var(--coin-image-size);
  position: relative;
  overflow: hidden;
  border-radius: 50%;

  &::after {
    content: '';
    position: absolute;
    inset: var(--shimmer-circle-inset);
    border-radius: 50%;
    background: linear-gradient(
      to bottom right,
      transparent 0%,
      transparent 40%,
      rgba(255, 255, 255, 0.32) 50%,
      transparent 60%,
      transparent 100%
    );
    background-size: 220% 220%;
    background-position: 140% 140%;
    background-repeat: no-repeat;
    animation: shimmer 2.5s ease-out infinite;
    pointer-events: none;
    z-index: 2;
  }

  @keyframes shimmer {
    0% {
      background-position: 140% 140%;
    }
    75% {
      background-position: -140% -140%;
    }
    100% {
      background-position: -140% -140%;
    }
  }
`;

const ClaimedCoinImage = styled(Image)`
  height: var(--coin-image-size);
  padding: 6px;
  width: var(--coin-image-size);
  z-index: 1;
`;

// Framed variant: same 173px footprint, no shimmer.
// - The badge PNG has transparency; the popup backdrop is dark, so without a fill the
//   dark shows through the frame's transparent inner hole. A white disc (z-index 0) sits
//   behind the badge to match how the same PNGs render in FramedMissionBadge on the
//   white wallet surface.
// - Disc size (146px = Math.round(173 * 0.845), radius 73px): sized to cover the
//   frame's transparent inner hole (max inner radius ≈73px). The ring's opaque outer
//   edge is not perfectly concentric and thins to ≈72.5px at one point, so the disc
//   overhangs the ring by ~0.5px there — sub-pixel and hidden under the ring's
//   anti-aliasing. Values measured from frame.png's alpha channel.
// - Inner image ratio (36/64 ≈ 56.25%) matches FramedMissionBadge's reference geometry
//   (36px image inside a 64px frame wrapper), keeping the badge comfortably inside the
//   white disc with no clipping needed.
const FRAMED_IMAGE_SIZE = 173;
const FRAMED_INNER_IMAGE_SIZE = Math.round(FRAMED_IMAGE_SIZE * (36 / 64)); // ≈ 97px – matches FramedMissionBadge ratio
const FRAMED_BACKGROUND_DISC_SIZE = Math.round(FRAMED_IMAGE_SIZE * 0.845); // ≈ 146px – covers frame's inner hole, stays under ring

const FramedImageWrapper = styled.div`
  --coin-image-size: ${FRAMED_IMAGE_SIZE}px;
  width: var(--coin-image-size);
  height: var(--coin-image-size);
  flex: 0 0 var(--coin-image-size);
  position: relative;
`;

const FramedBackgroundDisc = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${FRAMED_BACKGROUND_DISC_SIZE}px;
  height: ${FRAMED_BACKGROUND_DISC_SIZE}px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background-color: ${COLORS['Fill.White']};
  z-index: 0;
`;

const FramedCoinImage = styled(Image)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${FRAMED_INNER_IMAGE_SIZE}px;
  height: ${FRAMED_INNER_IMAGE_SIZE}px;
  object-fit: contain;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const FramedOverlay = styled.div`
  position: absolute;
  inset: 0;
  width: var(--coin-image-size);
  height: var(--coin-image-size);
  background-image: url(${frame});
  background-size: var(--coin-image-size) var(--coin-image-size);
  background-repeat: no-repeat;
  background-position: center;
  pointer-events: none;
  z-index: 2;
`;
